import React from 'react';
import { Shield, CheckCircle, Clock, Award, ArrowRight, Search, FileCheck } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ProcessSlider } from '../components/ProcessSlider';

export const TrademarkRegistration = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-4 py-2 rounded-full mb-6">
              <Shield className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-semibold">Professional Trademark Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Complete Trademark Registration <span className="text-yellow-400">Services</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Protect your brand with our comprehensive trademark registration service. We handle everything from search to filing, ensuring your intellectual property is secure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl"
              >
                Start Registration Now
              </button>
              <button
                onClick={onOpenPopup}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop"
                alt="Trademark registration"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Why Register Your <span className="text-red-600">Trademark?</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A registered trademark is your legal fortress against brand theft and misuse. It grants you exclusive nationwide rights to your brand name, logo, or slogan, empowering you to take legal action against infringers and protecting your market position.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Without trademark registration, competitors can legally use similar names and confuse your customers. Don't risk losing the brand you've worked so hard to build—secure your trademark protection today.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900">Exclusive Rights</div>
                    <div className="text-gray-600 text-sm">Nationwide protection</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900">Legal Protection</div>
                    <div className="text-gray-600 text-sm">Sue infringers</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900">Brand Value</div>
                    <div className="text-gray-600 text-sm">Increase credibility</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-gray-900">Asset Building</div>
                    <div className="text-gray-600 text-sm">Valuable IP asset</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Key <span className="text-red-600">Benefits</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how trademark registration strengthens your business and protects your brand identity.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Legal Protection',
                description: 'Federal registration provides nationwide protection and legal presumption of ownership, giving you the upper hand in disputes.'
              },
              {
                icon: Award,
                title: 'Brand Recognition',
                description: 'Build trust and credibility with the ® symbol, signaling to customers that your brand is legitimate and protected.'
              },
              {
                icon: Clock,
                title: 'Long-Term Rights',
                description: 'Enjoy indefinite protection as long as you maintain your trademark, ensuring lasting brand security for generations.'
              },
              {
                icon: CheckCircle,
                title: 'Market Exclusivity',
                description: 'Prevent competitors from using similar marks in your industry, maintaining your unique position in the marketplace.'
              },
              {
                icon: ArrowRight,
                title: 'Business Asset',
                description: 'A registered trademark is a valuable asset that can be licensed, franchised, or sold, adding substantial value to your business.'
              },
              {
                icon: Shield,
                title: 'Online Protection',
                description: 'Protect your brand on social media, e-commerce platforms, and domain registrations with federal trademark rights.'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="bg-red-100 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-red-600">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've streamlined trademark registration to make it simple, fast, and stress-free.
            </p>
          </div>
          <ProcessSlider />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transparent <span className="text-red-600">Pricing</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the package that fits your needs. No hidden fees, no surprises—just honest, straightforward pricing.
            </p>
          </div>
          <PricingSection onOpenPopup={onOpenPopup} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - FAQ Content */}
            <div className="space-y-6">
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Frequently Asked <span className="text-red-600">Questions</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Get answers to common questions about trademark registration services.
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {[
                  {
                    q: 'How long does trademark registration take?',
                    a: 'The USPTO typically takes 8-12 months to process a trademark application. However, we file your application within 24-48 hours of receiving your information, and you\'ll receive a filing confirmation immediately.'
                  },
                  {
                    q: 'What can be trademarked?',
                    a: 'You can trademark names, logos, slogans, brand names, product names, service names, and even colors or sounds if they identify your brand. The mark must be distinctive and not merely descriptive.'
                  },
                  {
                    q: 'Do I need a trademark attorney?',
                    a: 'While not legally required, working with a trademark attorney significantly increases your approval chances. Our attorneys ensure proper classification, comprehensive searches, and professional responses to USPTO office actions.'
                  },
                  {
                    q: 'What\'s the difference between TM and ®?',
                    a: 'TM indicates an unregistered trademark claim, while ® can only be used after your trademark is officially registered with the USPTO. The ® symbol provides notice of your federal registration rights.'
                  },
                  {
                    q: 'How much does the government filing fee cost?',
                    a: 'USPTO filing fees range from $250-$350 per class of goods/services. Our service fees are separate and cover the comprehensive search, application preparation, filing, and monitoring services.'
                  }
                ].map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg shadow-md border-none">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <span className="text-lg font-bold text-gray-900">{faq.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-600 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Right Side - Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=1000&fit=crop"
                alt="Trademark registration FAQs"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-gray-900 p-6 rounded-xl shadow-xl max-w-xs">
                <div className="text-3xl font-bold mb-1">98%</div>
                <div className="text-sm font-semibold">Approval Rate with Expert Guidance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Secure Your Trademark?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses that have protected their brands with Global Mark Registry. Get started today!
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
          >
            Start Your Application Now
          </button>
        </div>
      </section>
    </div>
  );
};